import type { Node } from "@mapcraft-tasks/client-store";
import { useConflictedNodes, useDispatch, useStore } from "@mapcraft-tasks/client-store/react";
import { isOverdue, isTerminal } from "@mapcraft-tasks/domain";
import { memo, type RefObject, useRef } from "react";
import { useOutline } from "../../outline/controller";
import type { Row as RowModel } from "../../outline/flatten";
import { Checkbox } from "../checkbox";
import { shortDate } from "../date-badge";
import { RowTags } from "../tag-chip";
import { RenderedTitle } from "./rendered-title";
import { TitleEditor } from "./title-editor";

/**
 * Отступ уровня подобран под ширину жёлоба (шеврон 15px + буллет 15px): направляющая
 * нить предка тогда всегда левее жёлоба потомка и ни на что не наезжает. У Tana ровно
 * та же зависимость — `--levelIndent` там 2.1rem при буллете 15px.
 */
export const INDENT = 26;
export const GUTTER_LEFT = 8;
/** Центр буллета внутри жёлоба — по нему и идёт нить. */
export const GUIDE_OFFSET = 22;

export const OutlineRow = memo(function OutlineRow({ row, today }: { row: RowModel; today: string }) {
	const { state, actions } = useOutline();
	const dispatch = useDispatch();
	const store = useStore();
	const rowRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLSpanElement>(null);
	const touch = useTouchRow({ row, rowRef, titleRef });

	const selected = state.cursorKey === row.key || state.selectedKeys.has(row.key);
	const editing = state.mode === "edit" && state.editing?.key === row.key;
	const node = row.node;
	const done = node.status === "done";
	const cancelled = node.status === "cancelled";
	const overdue = isOverdue(node, today);
	const cut = actions.clipboard?.mode === "cut" && actions.clipboard.nodeIds.includes(node.id);
	const conflicted = useConflictedNodes().has(node.id);

	return (
		<div
			ref={rowRef}
			className="row group"
			data-selected={selected}
			// Правку на телефоне видно только по строке: аккордов нет, каретка тонкая, а
			// подсветка «выбрано» там же, где и в навигации. Отсюда отдельный признак.
			data-editing={editing || undefined}
			data-flash={state.flashing.has(row.key)}
			style={{ paddingLeft: GUTTER_LEFT + row.depth * INDENT }}
			onPointerDown={touch.onPointerDown}
			onPointerMove={touch.onPointerMove}
			onPointerUp={touch.onPointerUp}
			onPointerCancel={touch.onPointerCancel}
			onClick={touch.onClick}
			onMouseDown={(e) => {
				if (touch.fromTouch()) {
					return; // палец разбирается сам: у него тап — это правка, а не выделение
				}
				// `Element`, а не `HTMLElement`: галочка и шеврон — это SVG внутри кнопки.
				const target = e.target instanceof Element ? e.target : null;
				if (target?.closest("input, textarea")) {
					return; // внутри редактора мышь принадлежит тексту, а не строке
				}

				// Шеврон, буллет и галочка делают своё дело сами. Курсор им всё же двигаем:
				// галочка снимается с ВЫДЕЛЕНИЯ, и без этого щелчок по чужой строке
				// отметил бы выделенную раньше.
				if (target?.closest("button") === null) {
					// `preventDefault` — чтобы браузер не тянул поверх своё выделение текста:
					// с `Shift` он растягивает его на полстраницы. (Когда появится `Shift`+drag
					// из ui-spec, диапазон уедет на mouseup — щелчок от протяжки отличается там.)
					if (e.shiftKey) {
						e.preventDefault();
						actions.selectTo(row.key);
						return;
					}
					if (e.metaKey || e.ctrlKey) {
						e.preventDefault();
						actions.toggleSelect(row.key);
						return;
					}
					if (e.detail === 2) {
						// Правку открываем на mousedown, но браузеру доиграть своё нельзя:
						// его фокус по умолчанию тут же уводит каретку из открытого input,
						// и режим правки схлопывается в тот же кадр.
						e.preventDefault();
						actions.setMode("edit");
						actions.setEditing({ key: row.key, caret: null });
						return;
					}
				}

				actions.focusRow(row.key);
			}}
		>
			{/* Нити от предков — по одной на уровень. Волосок, а не кликабельная зона. */}
			{Array.from({ length: row.depth }, (_, level) => (
				<span
					key={level}
					className="indent-guide"
					style={{ left: GUTTER_LEFT + level * INDENT + GUIDE_OFFSET }}
				/>
			))}

			{/* Жёлоб: шеврон появляется по наведению, буллет проваливает в ноду. */}
			<span className="flex shrink-0 items-center self-start pt-[3px]">
				{/* Наведения на телефоне нет — шеврон там виден всегда, иначе свернуть ветку
				    нечем вообще. */}
				<button
					type="button"
					aria-label={row.expanded ? "свернуть" : "развернуть"}
					className="btn-icon size-[15px] rounded-xs opacity-0 transition-opacity group-hover:opacity-100 max-md:opacity-100"
					onClick={(e) => {
						e.stopPropagation();
						actions.toggleCollapse(row.key);
					}}
				>
					{row.hasChildren && <Chevron open={row.expanded} />}
				</button>

				{/*
				 * Буллет говорит тремя каналами сразу, и разводить их обязательно:
				 * ФОРМА ядра — про ноду (кольцо = проект, точка = всё остальное), ЦВЕТ ядра —
				 * жива ли она (терминальность) и не фокус ли это, ОРБИТА — как строка сюда
				 * попала (пунктир = вплетение). Вплетённый выполненный проект обязан
				 * показать все три разом.
				 * Сам вид — в `.bullet` (styles.css).
				 */}
				<button
					type="button"
					aria-label="провалиться"
					className="bullet relative"
					data-kind={node.role === "project" ? "project" : node.role === "focus" ? "focus" : undefined}
					data-linked={row.placementId ? "" : undefined}
					data-tone={done || cancelled ? "muted" : undefined}
					onClick={(e) => {
						e.stopPropagation();
						actions.zoom(node.id);
					}}
				>
					{/* Ореол — «внутри что-то есть, и оно сейчас свёрнуто». */}
					{row.hasChildren && !row.expanded && <span className="bullet-halo" />}
					<span className="bullet-dot relative" />
				</button>
			</span>

			<span className={`min-w-0 ${cut ? "opacity-40" : ""}`}>
				<span className="flex items-baseline gap-[6px]">
					{/* Ключ строки обязателен: галочка адресна, а курсора под пальцем может не быть. */}
					<Checkbox node={node} style={{ marginTop: 3 }} onToggle={() => actions.toggleDone(row.key)} />
					<span
						ref={titleRef}
						className={[
							"row-title min-w-0 flex-1",
							// В чтении заголовок обрезается многоточием, но тот же `overflow: hidden`
							// съедал бы подсказку `[[`-автокомплита: она висит под строкой внутри
							// этого же блока. В правке обрезать нечего — прокрутка своя у поля.
							editing ? "overflow-visible" : "",
							done ? "text-ink-muted line-through decoration-ink-faint" : "",
							cancelled ? "text-ink-faint line-through decoration-danger/60" : "",
							node.status === "someday" || node.status === "paused" ? "text-ink-muted" : ""
						].join(" ")}
					>
						{editing ? (
							<TitleEditor
								value={node.title}
								caret={state.editing?.caret ?? null}
								placeholder="…"
								onCommit={(raw) => {
									if (raw !== node.title) {
										dispatch({
											name: "renameNode",
											input: {
												nodeId: node.id,
												title: raw,
												baseContentVersion: store.contentVersionOf(node.id)
											}
										});
									}
								}}
								onCancel={() => {
									if (!actions.stopEditing(row.key)) {
										return; // редактор уже уехал на новый ключ — это не наш выход
									}
									// `Esc` на нетронутом черновике = «передумал»: строку убираем.
									actions.discardDraft(node.id);
								}}
								onKeyDown={(e) => {
									// Enter НЕ режет строку — режет `Alt+Enter` по каретке.
									if (e.key === "Enter" && !e.altKey) {
										e.preventDefault();
										actions.setMode("nav");
										actions.setEditing(null);
										actions.createSibling(e.shiftKey ? "child" : "below");
									}
									if (e.key === "Tab") {
										e.preventDefault();
										// Отступ не выкидывает из редактора. Строка переезжает, её ключ
										// (это путь) меняется, редактор пересоздаётся — поэтому каретку
										// запоминаем смещением, а на новый ключ `editing` переведёт
										// контроллер внутри `indent`/`outdent`.
										actions.setEditing({
											key: row.key,
											caret: e.currentTarget.selectionStart ?? "end"
										});
										e.shiftKey ? actions.outdent() : actions.indent();
									}
								}}
							/>
						) : node.title.trim() === "" ? (
							// Пустую строку не подписываем — её видно по буллету.
							// Неразрывный пробел нужен только чтобы строка сохранила высоту и осталась кликабельной.
							<span>{"\u00a0"}</span>
						) : (
							<RenderedTitle text={node.title} onOpenMention={actions.zoom} />
						)}
					</span>
				</span>

				{state.openBodies.has(row.key) && <BodyEditor node={node} rowKey={row.key} />}
				{node.body !== null && !state.openBodies.has(row.key) && (
					<span className="mt-px block truncate text-xs font-light text-ink-muted">{node.body}</span>
				)}
			</span>

			<span className="tabular flex shrink-0 items-center gap-2 self-start pt-[3px] text-xs font-light">
				{conflicted && (
					<span className="text-danger" title="операция отклонена — разберите в «Конфликтах»">
						⚠
					</span>
				)}
				{node.tags.length > 0 && (
					<RowTags
						tags={node.tags}
						onOpen={() => {
							// Строка внутри мультивыделения — правим всё выделение, как `tt`;
							// иначе — ровно эту ноду, прошлое выделение тут ни при чём.
							if (!state.selectedKeys.has(row.key)) {
								actions.focusRow(row.key);
							}
							actions.setOverlay({ kind: "tags" });
						}}
					/>
				)}
				{node.recurrence && (
					<span className="text-positive" title="повторяющаяся">
						↻ {node.recurrence.count ?? 1}/{periodLabel(node.recurrence.period)}
					</span>
				)}
				{node.scheduledDate && (
					<span
						className={
							overdue ? "text-danger" : isTerminal(node.status) ? "text-ink-faint" : "text-ink-muted"
						}
					>
						{shortDate(node.scheduledDate, today)}
					</span>
				)}
				{row.placementId && <PlacementBadge nodeId={node.id} />}
				{row.cyclic && (
					<span className="text-ink-faint" title="уже выше по ветке">
						↻
					</span>
				)}
				{node.archivedAt && <span className="text-ink-faint">архив</span>}
			</span>
		</div>
	);
});

/** Жест считается горизонтальным после этого сдвига — раньше это ещё прокрутка. */
const SWIPE_ENGAGE = 12;
/** Дальше этого — сработает отступ. Примерно два уровня вложенности на глаз. */
const SWIPE_TRIGGER = 56;
const SWIPE_MAX = 88;

/**
 * Строка под пальцем.
 *
 * Два жеста, и оба берут на себя работу клавиатуры:
 *
 *  - **тап по тексту — правка.** Это ответ на «как вызвать клавиатуру»: на десктопе
 *    в редактор входят `Enter`ом или двойным щелчком, и оба под пальцем недоступны.
 *    Каретка встаёт в место касания, а не в конец: чаще всего строку открывают,
 *    чтобы поправить слово, а не дописать.
 *  - **протянуть вбок — отступ и выступ.** `Tab` на телефоне нет, а иерархия —
 *    это половина смысла аутлайнера. Ход вязнет за порогом: видно, что дальше незачем.
 *
 * Мышь сюда не заходит вовсе — у неё свой разбор в `onMouseDown` строки, где двойной
 * щелчок, `Shift` и `Cmd` значат своё.
 */
function useTouchRow({
	row,
	rowRef,
	titleRef
}: {
	row: RowModel;
	rowRef: RefObject<HTMLDivElement | null>;
	titleRef: RefObject<HTMLSpanElement | null>;
}) {
	const { state, actions } = useOutline();
	const start = useRef<{ x: number; y: number } | null>(null);
	const swiping = useRef(false);
	const shift = useRef(0);
	const touched = useRef(false);
	/* Жест уже что-то сделал — щелчок следом не должен открыть правку поверх. */
	const acted = useRef(false);

	const editing = state.mode === "edit" && state.editing?.key === row.key;

	const paint = (offset: number) => {
		const element = rowRef.current;
		if (element) {
			element.style.transform = offset === 0 ? "" : `translateX(${offset}px)`;
			element.style.transition = "none";
		}
	};

	const settle = () => {
		const element = rowRef.current;
		if (element) {
			element.style.transform = "";
			element.style.transition = "transform 120ms ease-out";
		}
	};

	return {
		fromTouch: () => touched.current,

		onPointerDown(event: React.PointerEvent) {
			touched.current = event.pointerType !== "mouse";
			acted.current = false;
			swiping.current = false;
			shift.current = 0;
			start.current = touched.current && !editing ? { x: event.clientX, y: event.clientY } : null;
		},

		onPointerMove(event: React.PointerEvent) {
			if (!start.current) {
				return;
			}
			const dx = event.clientX - start.current.x;
			const dy = event.clientY - start.current.y;
			if (!swiping.current) {
				if (Math.abs(dx) < SWIPE_ENGAGE || Math.abs(dx) <= Math.abs(dy)) {
					return; // вертикаль сильнее — это прокрутка, отдаём браузеру
				}
				swiping.current = true;
				// Жест адресный: он про ту строку, которую тянут, а не про прошлое выделение.
				actions.focusRow(row.key);
			}
			shift.current = dx;
			paint(rubber(dx));
		},

		onPointerUp() {
			start.current = null;
			if (!swiping.current) {
				return;
			}
			swiping.current = false;
			acted.current = true;
			settle();
			if (shift.current > SWIPE_TRIGGER) {
				actions.indent();
			} else if (shift.current < -SWIPE_TRIGGER) {
				actions.outdent();
			}
		},

		onPointerCancel() {
			start.current = null;
			swiping.current = false;
			settle();
		},

		/*
		 * Правка открывается на `click`, а не на `pointerup`: Safari пускает
		 * программный `focus()` (а значит и клавиатуру) только из жеста, и щелчок —
		 * самый надёжный из них.
		 */
		onClick(event: React.MouseEvent) {
			if (!touched.current || acted.current || editing) {
				return;
			}
			const target = event.target instanceof Element ? event.target : null;
			if (target?.closest("button, input, textarea, a")) {
				return; // галочка, буллет, шеврон и меншен делают своё
			}
			actions.focusRow(row.key);
			actions.setMode("edit");
			actions.setEditing({ key: row.key, caret: caretAt(row.node.title, titleRef.current, event) });
		}
	};
}

/** За порогом ход вязнет: тянуть дальше можно, толку с этого нет. */
function rubber(dx: number): number {
	const size = Math.abs(dx);
	const eased = size <= SWIPE_TRIGGER ? size : SWIPE_TRIGGER + (size - SWIPE_TRIGGER) / 3;
	return Math.sign(dx) * Math.min(eased, SWIPE_MAX);
}

/**
 * Куда поставить каретку по месту касания.
 *
 * Работает потому, что вне правки и в правке видна ОДНА И ТА ЖЕ строка символов:
 * меншен и там и там показан заголовком. Ломает это только инлайн-markdown — он
 * вне фокуса отрендерен, а в редакторе сырой, и смещения разъезжаются ровно на
 * длину звёздочек. В таком заголовке честнее конец строки, чем каретка не там.
 */
function caretAt(title: string, container: HTMLElement | null, event: React.MouseEvent): number | "end" {
	if (container === null || /[*`~]/.test(title)) {
		return "end";
	}
	const point = caretPoint(event.clientX, event.clientY);
	if (point === null || !container.contains(point.node)) {
		return "end";
	}
	let before = 0;
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
	while (walker.nextNode()) {
		if (walker.currentNode === point.node) {
			return before + point.offset;
		}
		before += walker.currentNode.textContent?.length ?? 0;
	}
	return "end";
}

/** Две несовместимые реализации одного и того же; обе живые. */
function caretPoint(x: number, y: number): { node: globalThis.Node; offset: number } | null {
	if (typeof document.caretPositionFromPoint === "function") {
		const position = document.caretPositionFromPoint(x, y);
		return position ? { node: position.offsetNode, offset: position.offset } : null;
	}
	if (typeof document.caretRangeFromPoint === "function") {
		const range = document.caretRangeFromPoint(x, y);
		return range ? { node: range.startContainer, offset: range.startOffset } : null;
	}
	return null;
}

/**
 * Иконки рисуются в сетке 24 штрихом 2 и показываются в 14px — тонкие контурные,
 * не заливные; цвет наследуется от текста. Это рецепт иконок Tana целиком.
 */
export function Chevron({ open }: { open: boolean }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width={14}
			height={14}
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ transform: open ? "rotate(90deg)" : undefined }}
		>
			<path d="M9 6l6 6-6 6" />
		</svg>
	);
}

/**
 * Бейдж вплетения. Без него человек не поймёт, почему `Del` на одной строке убирает её
 * насовсем, а на соседней — только отсюда (ui-spec 8.2).
 */
function PlacementBadge({ nodeId }: { nodeId: string }) {
	const store = useStore();
	const { actions } = useOutline();
	const home = store.getSnapshot().replica.nodes.get(nodeId)?.parentId;
	const title = home ? store.getSnapshot().replica.nodes.get(home)?.title : undefined;
	return (
		<button
			type="button"
			className="text-accent transition-colors hover:text-ink-strong"
			title="вплетение — дом ноды в другом месте"
			onClick={(e) => {
				e.stopPropagation();
				if (home) {
					actions.zoom(home);
				}
			}}
		>
			↗ {title ?? "дом"}
		</button>
	);
}

/**
 * Заметка строки. Открывается по `nn`, закрывается ОТСЮДА — `Esc`, как и везде в системе.
 *
 * Без своего выхода поле оказывалось ловушкой: клавиши внутри `textarea` до аккордов не
 * доходят, а `nn` переключает строку под курсором, так что уехавший курсор оставлял поле
 * открытым насовсем. Пустая заметка вдобавок закрывается сама на блюре — держать на экране
 * пустое поле незачем, оно и в базе схлопывается в `null`.
 */
function BodyEditor({ node, rowKey }: { node: Node; rowKey: string }) {
	const dispatch = useDispatch();
	const store = useStore();
	const { actions } = useOutline();

	const save = (raw: string) => {
		const value = raw.trim() === "" ? null : raw;
		if (value !== node.body) {
			dispatch({
				name: "setBody",
				input: { nodeId: node.id, body: value, baseContentVersion: store.contentVersionOf(node.id) }
			});
		}
		return value;
	};

	return (
		<textarea
			// `nn` — это «править заметку», а не «показать поле»: без фокуса пришлось бы
			// доводить мышью то, что начато с клавиатуры.
			autoFocus
			defaultValue={node.body ?? ""}
			rows={3}
			placeholder="заметка · Esc — закрыть"
			className="field mt-1 block w-full resize-y text-sm font-light"
			onClick={(e) => e.stopPropagation()}
			onKeyDown={(e) => {
				if (e.key !== "Escape") {
					return; // Enter здесь — перевод строки: заметка многострочная, в отличие от заголовка
				}
				e.preventDefault();
				e.stopPropagation();
				save(e.currentTarget.value);
				actions.closeBody(rowKey);
				// Фокус обязан уйти из поля: в режиме навигации клавиши слушает документ,
				// а из `textarea` они туда не долетают.
				e.currentTarget.blur();
				actions.focusRow(rowKey);
			}}
			onBlur={(e) => {
				// Пустой body при потере фокуса схлопывается и пишется `null`.
				if (save(e.target.value) === null) {
					actions.closeBody(rowKey);
				}
			}}
		/>
	);
}

function periodLabel(period: "day" | "week" | "month"): string {
	return period === "day" ? "дн" : period === "week" ? "нед" : "мес";
}
